import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
} from '@mui/material'
import { ErrorOutline as ErrorIcon, Summarize as SummarizeIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { QueueSnapshot } from '@src/tools/dead-letter/types/QueueTypes'

type SidebarView = 'summary' | 'errors' | 'queue'

interface QueueSidebarProps {
  poisonQueues: QueueSnapshot[]
  isLoading: boolean
  selectedQueue: { storageAccountName: string; queueName: string } | null
  activeView: SidebarView
  errorCount: number
  onSelectSummary: () => void
  onSelectErrors: () => void
  onSelectQueue: (storageAccountName: string, queueName: string) => void
}

const QueueSidebar = ({ poisonQueues, isLoading, selectedQueue, activeView, errorCount, onSelectSummary, onSelectErrors, onSelectQueue }: QueueSidebarProps) => {
  const { t } = useTranslation()

  return (
    <Box sx={{ width: 260, minWidth: 260, borderRight: 1, borderColor: 'divider', overflow: 'auto', height: '100%' }}>
      <List dense>
        <ListItemButton selected={activeView === 'summary'} onClick={onSelectSummary}>
          <SummarizeIcon fontSize="small" sx={{ mr: 1 }} />
          <ListItemText primary={t('queues.summary')} slotProps={{ primary: { fontWeight: activeView === 'summary' ? 'bold' : 'normal' } }} />
        </ListItemButton>
        <ListItemButton selected={activeView === 'errors'} onClick={onSelectErrors}>
          <ErrorIcon fontSize="small" sx={{ mr: 1, color: errorCount > 0 ? 'error.main' : undefined }} />
          <ListItemText primary={t('queues.refreshErrors')} slotProps={{ primary: { fontWeight: activeView === 'errors' ? 'bold' : 'normal' } }} />
          {errorCount > 0 && (
            <Chip size="small" label={errorCount} color="error" sx={{ borderRadius: '12px', fontWeight: 600, minWidth: 32 }} />
          )}
        </ListItemButton>

        <ListSubheader sx={{ lineHeight: '32px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('queues.poisonQueues')}
        </ListSubheader>

        {isLoading && <CircularProgress size={24} sx={{ ml: 2 }} />}

        {!isLoading && poisonQueues.length === 0 && (
          <ListItemText
            primary={t('queues.noPoisonQueues')}
            sx={{ px: 2, py: 1, textAlign: 'center', color: 'text.secondary' }}
          />
        )}

        {poisonQueues.map((q) => {
          const isSelected = selectedQueue?.storageAccountName === q.storageAccountName && selectedQueue?.queueName === q.queueName
          return (
            <ListItemButton
              key={q.id}
              selected={isSelected}
              onClick={() => onSelectQueue(q.storageAccountName, q.queueName)}
            >
              <ListItemText
                primary={q.queueName}
                slotProps={{ primary: { fontSize: '0.8rem', color: 'error.main' } }}
              />
              <Chip
                size="small"
                label={q.messageCount}
                color="error"
                sx={{ borderRadius: '12px', fontWeight: 600, minWidth: 32 }}
              />
            </ListItemButton>
          )
        })}
      </List>
    </Box>
  )
}

export default QueueSidebar
